import z from "zod";
import { logisticsApi } from "@/api/ApiClient";
import { generateQueryString } from "@/api/Helpers";
import { type IPatientType } from "@medoma/models/patients";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { fetchPatient, fetchPatients } from "@/api/Patients";
import {
  activityOccurrenceAndGroupKeys,
  type INewActivity,
} from "@/api/Activities";
import {
  getActivityCategoryConfig,
  isGroup,
  type IActivityCategory,
} from "@medoma/models/activities";
import {
  type IInvoiceStatus,
  type IInvoicingCodeType,
  type IOptionalInvoicingCodes,
  invoicingCodeSchema,
  activityInvoicingCodesSchema,
  occurrenceInvoicingCodesSchema,
  invoiceStatusSchema,
} from "@medoma/models/invoicing";
import {
  unenrichedInvoicingActivityOccurrenceOrGroupSchema,
  type IUnenrichedInvoicingActivityOccurrenceOrGroup,
} from "@medoma/models/invoicing.unenriched";

const invoicingActivityOccurrenceOrGroupKeys = {
  all: () => [...activityOccurrenceAndGroupKeys.all(), "invoicing"] as const,
  lists: () =>
    [...invoicingActivityOccurrenceOrGroupKeys.all(), "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...invoicingActivityOccurrenceOrGroupKeys.lists(), filters] as const,
};

export type INewActivityWithOptionalInvoicingCodes = INewActivity &
  IOptionalInvoicingCodes;

async function fetchInvoicingCodes({
  patientType,
  codeType,
}: {
  patientType: IPatientType | null;
  codeType: IInvoicingCodeType | null;
}) {
  const queryString = generateQueryString({ codeType, patientType });
  return await logisticsApi
    .get(`/invoicing/invoicing-codes${queryString}`)
    .json(z.array(invoicingCodeSchema));
}

export const useInvoicingCodesFromPatientId = ({
  patientId,
  codeType,
}: {
  patientId: string;
  codeType: IInvoicingCodeType | null;
}) => {
  return useQuery({
    queryKey: ["invoicingCodes", patientId, codeType],
    queryFn: async () => {
      return fetchPatient(patientId).then((patient) =>
        fetchInvoicingCodes({
          patientType: patient.patientType,
          codeType,
        }),
      );
    },
  });
};

export const useInvoicingCodes = ({
  patientType,
  codeType,
}: {
  patientType: IPatientType;
  codeType: IInvoicingCodeType | null;
}) => {
  return useQuery({
    queryKey: ["invoicingCodes", patientType, codeType],
    queryFn: async () => {
      return fetchInvoicingCodes({
        patientType,
        codeType,
      });
    },
  });
};

type ISaveActivityInvoicingCode = {
  activityId: string;
  patientId: string;
  procedureCodes: string[];
  productCodes: string[];
};

export const saveActivityInvoicingCodes = async ({
  activityId,
  patientId,
  procedureCodes,
  productCodes,
}: ISaveActivityInvoicingCode) => {
  await logisticsApi.post(`/invoicing/activities/${activityId}`, {
    json: { patientId, procedureCodes, productCodes },
  });
};

export const saveMultipleActivitiesInvoicingCodes = async (
  activities: ISaveActivityInvoicingCode[],
) => {
  await logisticsApi.post(`/invoicing/activities/bulk`, {
    json: { activities },
  });
};

export const approveInvoicingOccurrences = async (occurrenceIds: string[]) => {
  await logisticsApi.post(`/invoicing/occurrences/approve`, {
    json: { occurrenceIds },
  });
};

export const setGroupNotInvoiceable = async (groupId: string) => {
  await logisticsApi.post(`/invoicing/groups/${groupId}/not-invoiceable`);
};

export const setOccurrenceNotInvoiceable = async (occurrenceId: string) => {
  await logisticsApi.post(
    `/invoicing/occurrences/${occurrenceId}/not-invoiceable`,
  );
};

const fetchOccurrenceInvoiceCodes = async (occurrenceId: string) => {
  return await logisticsApi
    .get(`/invoicing/occurrences/${occurrenceId}/invoicing-codes`)
    .json(occurrenceInvoicingCodesSchema);
};

const fetchActivityInvoicingCodes = async (activityId: string) => {
  return await logisticsApi
    .get(`/invoicing/activities/${activityId}/invoicing-codes`)
    .json(activityInvoicingCodesSchema);
};

/**
 * NOTE ON INVOICING CODES FOR ACTIVITIES AND OCCURRENCES:
 * Use for activity series (recurring activities).
 * For the occurrence of a recurring activity, AND for one-time activities, use useOccurrenceInvoicingCodes.
 * The `occurrence` endpoint in fetchOccurrenceInvoicingCodes will "lock" the invoicing codes for the occurrence
 * if the occurrence has been reported to the invoicing system (backend handles this).
 * The `activity` endpoint in fetchActivityInvoicingCodes will not (if invoicing codes for example are edited on the Series).
 */
export const useActivityInvoicingCodes = (activityId: string) => {
  return useQuery({
    queryKey: ["activityInvoicingCodes", activityId],
    queryFn: () => fetchActivityInvoicingCodes(activityId),
  });
};

export const useOccurrenceInvoicingCodes = (occurrenceId: string) => {
  return useQuery({
    queryKey: ["occurrenceInvoicingCodes", occurrenceId],
    queryFn: () => fetchOccurrenceInvoiceCodes(occurrenceId),
  });
};

export const shouldSaveInvoice = ({
  invoicingActivated,
  category,
  procedureCodes,
  productCodes,
  activityHasPatient,
}: {
  invoicingActivated: boolean;
  category: IActivityCategory;
  procedureCodes: string[];
  productCodes: string[];
  activityHasPatient: boolean;
}) => {
  const { allowsInvoicingCode } = getActivityCategoryConfig({
    category,
    activityHasPatient,
  });

  return (
    invoicingActivated &&
    allowsInvoicingCode &&
    (procedureCodes.length > 0 || productCodes.length > 0)
  );
};

async function fetchInvoicingOccurrencesAndGroups({
  invoiceStatuses,
  patientId,
}: {
  invoiceStatuses: IInvoiceStatus[];
  patientId?: string;
}) {
  const queryString = generateQueryString({
    status: invoiceStatuses,
    patientId,
  });
  const parsedData = await logisticsApi
    .get(`/invoicing/occurrences${queryString}`)
    .json(z.array(unenrichedInvoicingActivityOccurrenceOrGroupSchema));

  return parsedData;
}

const enrichAndParseInvocingOccurrencesAndGroups = async (
  invoicingOccurrencesAndGroups: IUnenrichedInvoicingActivityOccurrenceOrGroup[],
) => {
  const patientIds = invoicingOccurrencesAndGroups
    .map((invoicingOccurrenceOrGroup) => invoicingOccurrenceOrGroup.patientId)
    .filter((patientId) => !!patientId);

  const patients = await fetchPatients({ patientIds });

  // Invoicing rows are billed to a patient, so every row has to resolve to one.
  // The id it was found by is dropped rather than carried alongside: the row now
  // holds the patient, and a copy of the id could only ever go stale.
  return invoicingOccurrencesAndGroups.map(
    ({ patientId, ...invoicingOccurrenceOrGroup }) => {
      const patient = patients.find((patient) => patient.id === patientId);

      if (!patient) {
        throw new Error(`Could not resolve the patient on ${patientId}.`);
      }

      return {
        ...invoicingOccurrenceOrGroup,
        patient,
      };
    },
  );
};

export const invoicingOccurrencesAndGroupsOptions = ({
  invoicingStatuses,
}: {
  invoicingStatuses: IInvoiceStatus[];
}) =>
  queryOptions({
    queryKey: invoicingActivityOccurrenceOrGroupKeys.list({
      status: invoicingStatuses,
    }),
    queryFn: async () =>
      enrichAndParseInvocingOccurrencesAndGroups(
        await fetchInvoicingOccurrencesAndGroups({
          invoiceStatuses: invoicingStatuses,
        }),
      ),
  });

async function fetchInvoicingOccurrencesAndGroupsToApprove() {
  const parsedData = await logisticsApi
    .get(`/invoicing/occurrences/to-approve`)
    .json(z.array(unenrichedInvoicingActivityOccurrenceOrGroupSchema));

  return parsedData;
}

export const invoicingOccurrencesAndGroupsToApproveOptions = queryOptions({
  queryKey: invoicingActivityOccurrenceOrGroupKeys.list({
    perspective: "to-approve",
  }),
  queryFn: async () =>
    enrichAndParseInvocingOccurrencesAndGroups(
      await fetchInvoicingOccurrencesAndGroupsToApprove(),
    ),
});

export const finishedInvoicingCodesForPatientOptions = ({
  patientId,
}: {
  patientId: string;
}) => {
  const invoicingStatusesIndicatingFinishedActivityOccurrence = [
    invoiceStatusSchema.enum.awaitingApproval,
    invoiceStatusSchema.enum.completed,
    invoiceStatusSchema.enum.error,
    invoiceStatusSchema.enum.pending,
  ];
  return queryOptions({
    queryKey: invoicingActivityOccurrenceOrGroupKeys.list({
      status: invoicingStatusesIndicatingFinishedActivityOccurrence,
      patientId,
    }),
    queryFn: () =>
      fetchInvoicingOccurrencesAndGroups({
        invoiceStatuses: invoicingStatusesIndicatingFinishedActivityOccurrence,
        patientId,
      }).then((occurrencesAndGroups) =>
        occurrencesAndGroups
          .flatMap((occurrenceOrGroup) => {
            if (isGroup(occurrenceOrGroup)) {
              const group = occurrenceOrGroup;
              return group.occurrences.flatMap(
                ({ procedureCodes, productCodes }) => [
                  procedureCodes,
                  productCodes,
                ],
              );
            }
            const { procedureCodes, productCodes } = occurrenceOrGroup;
            return [procedureCodes, productCodes];
          })
          .flat()
          .map(({ code }) => code),
      ),
  });
};
